import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
    decodeColumns,
    decodeFilterState,
    encodeColumns,
    encodeFilterState,
    type PlanIndexFilterState,
} from 'utils/planIndexFilterParams';

import { useTpoGenConfig } from '../../../context/CustomerConfigContext';

export interface PlanIndexUrlState extends PlanIndexFilterState {
    searchKeyword: string;
    setSearchKeyword: (value: string) => void;
    showMyPlans: boolean;
    setShowMyPlans: (value: boolean) => void;
    selectedColumns: string[];
    setSelectedColumns: (value: string[]) => void;
    setAppliedFilters: (result: PlanIndexFilterState) => void;
    resetPlanIndexFilters: () => void;
}

/**
 * Backs all Plan Index view state (search, my-plans, columns, filters) with the
 * URL query string so it survives navigation, refresh and back/forward.
 *
 * react-router-dom is ^6.3.0, so the functional setSearchParams updater is not
 * available: each setter clones the current params, mutates one key, and writes
 * with { replace: true } so individual tweaks do not stack history entries.
 */
export function usePlanIndexUrlState(): PlanIndexUrlState {
    const { planIndexColumns, planIndexFilterKeys } = useTpoGenConfig();
    const [searchParams, setSearchParams] = useSearchParams();

    const defaultVisibleColumns = useMemo(
        () => planIndexColumns.filter((c) => c.defaultVisible).map((c) => c.key),
        [planIndexColumns],
    );
    const filterKeys = useMemo(() => planIndexFilterKeys.map((f) => f.value), [planIndexFilterKeys]);

    const searchKeyword = searchParams.get('q') ?? '';
    const showMyPlans = searchParams.get('myPlans') === '1';

    const colsRaw = searchParams.get('cols');
    const selectedColumns = useMemo(
        () => decodeColumns(colsRaw, defaultVisibleColumns),
        [colsRaw, defaultVisibleColumns],
    );

    const filtersRaw = searchParams.get('filters');
    const { allCheckedAttribute, allSelectedValueForAllAttribute, applyFilterObject } = useMemo(
        () => decodeFilterState(filtersRaw, filterKeys),
        [filtersRaw, filterKeys],
    );

    const updateParams = useCallback(
        (mutate: (params: URLSearchParams) => void) => {
            const next = new URLSearchParams(searchParams);
            mutate(next);
            setSearchParams(next, { replace: true });
        },
        [searchParams, setSearchParams],
    );

    const setSearchKeyword = useCallback(
        (value: string) =>
            updateParams((params) => {
                if (value) params.set('q', value);
                else params.delete('q');
            }),
        [updateParams],
    );

    const setShowMyPlans = useCallback(
        (value: boolean) =>
            updateParams((params) => {
                if (value) params.set('myPlans', '1');
                else params.delete('myPlans');
            }),
        [updateParams],
    );

    const setSelectedColumns = useCallback(
        (value: string[]) =>
            updateParams((params) => {
                const encoded = encodeColumns(value, defaultVisibleColumns);
                if (encoded) params.set('cols', encoded);
                else params.delete('cols');
            }),
        [updateParams, defaultVisibleColumns],
    );

    const setAppliedFilters = useCallback(
        (result: PlanIndexFilterState) =>
            updateParams((params) => {
                const encoded = encodeFilterState(result);
                if (encoded) params.set('filters', encoded);
                else params.delete('filters');
            }),
        [updateParams],
    );

    const resetPlanIndexFilters = useCallback(
        () =>
            updateParams((params) => {
                params.delete('filters');
            }),
        [updateParams],
    );

    return {
        searchKeyword,
        setSearchKeyword,
        showMyPlans,
        setShowMyPlans,
        selectedColumns,
        setSelectedColumns,
        allCheckedAttribute,
        allSelectedValueForAllAttribute,
        applyFilterObject,
        setAppliedFilters,
        resetPlanIndexFilters,
    };
}
